<template>
  <div class="pinned-user-notification">
    <NotificationLayout>
      <template #icon>
        <UserIcon class="user-icon" :userId="data.data.pinnedUserId" userStatus="dark" />
      </template>

      <template #content>
        <div class="notification-header">
          <Name :name="name" :pronouns="pronouns" :data="data"/>
        </div>
        <div class="notification-body">
          {{ message }}

          <div class="status-and-button">
            <div v-if="status" class="status" v-html="status.text" :class="[`status--${status.colour}`]"></div>
            <PinButton
              v-if="canUnpin"
              @mouseenter="hoveringPin = true"
              @mouseleave="hoveringPin = false"
              :initialPinId="data.data.pinsId"
              :userId="userId"
            />
          </div>
        </div>
      </template>

      <template #footer>
        <div v-if="userIsAvailable && !invited">
          <FooterButtons :buttons="footerButtons" />
        </div>
      </template>
    </NotificationLayout>
  </div>
</template>
<script>
// Wrapper for a sidebar notification.
import { mapActions } from 'vuex';
import Name from './parts/Name.vue';
import FooterButtons from './parts/FooterButtons.vue';
import UserIcon from '@/components/UserIcon.vue';
import NotificationLayout from './parts/NotificationLayout.vue';
import PinButton from '@/components/atoms/PinButton.vue';

export default {
  name: 'PinnedUser',
  props: {
    data: Object
  },
  data() {
    return {
      hoveringPin: false
    };
  },
  components: {
    NotificationLayout,
    UserIcon,
    Name,
    FooterButtons,
    PinButton
  },
  computed: {
    name() {
      return `${this.data.data.firstName} ${this.data.data.lastName}`;
    },
    pronouns() {
      return this.data?.data?.pronouns || '';
    },
    canUnpin() {
      return !this.expired && !this.cancelled && !this.userIsAvailable && !this.invited
    },
    message() {
      if (this.hoveringPin) {
        return "Cancel pin"
      }

      return 'You pinned';
    },
    userIsAvailable() {
      return this.data.response === 'Available'
    },
    userIsUnavailable() {
      return this.data.data.status === 'unavailable'
    },
    userIsOffline() {
      return this.data.response === 'Offline'
    },
    expired() {
      return this.data.response === 'Expired'
    },
    cancelled() {
      return this.data.response === 'Cancelled'
    },
    invited() {
      return this.data.response === 'Handled'
    },
    userId() {
      return this.data.recipientId
    },
    status() {
      if (this.cancelled) {
        return {
          text: 'You cancelled pin',
          colour: 'red'
        };
      }

      if (this.expired) {
        return {
          text: 'Pin expired',
          colour: 'grey'
        };
      }

      if (this.invited) {
        return {
          text: 'You invited',
          colour: "green"
        }
      }

      if (this.userIsAvailable) {
        return {
          text: this.data.data.firstName + ' is now available',
          colour: 'green'
        };
      }

      if (this.userIsUnavailable) {
        let text = "Busy"

        if (this.data.data.pinnedUserSpaceName && this.data.data.pinnedUserSpaceName != "") {
          text = `Busy in <u>${this.data.data.pinnedUserSpaceName}</u>`
        }

        return {
          text,
          colour: 'red'
        };
      }

      return {
        text: "Offline",
        colour: "grey"
      }
    },
    footerButtons() {
      return [
        {
          label: 'Cancel pin',
          colour: 'ghost',
          icon: require(`@/assets/global-icons/cross-white.svg`),
          onClick: this.cancelPin
        },
        {
          label: 'Invite to Room',
          colour: 'green',
          icon: require(`@/assets/notification-icons/camcorder-white.svg`),
          onClick: this.inviteToRoom
        }
      ];
    }
  },
  methods: {
    ... mapActions(['cancelPinHandler', 'invitePinnedUserHandler']),
    async inviteToRoom() {
      this.invitePinnedUserHandler(this.data)
    },
    async cancelPin() {
      await this.cancelPinHandler([this.data.data.pinsId])
    }
  }
};
</script>
<style scoped lang="scss">
@import '~@/scss/components/notifications/notification_styles.scss';

.status-and-button {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-icon {
  box-sizing: border-box;
  width: 45px;
  height: 45px;
}

.fade-enter-active,
.fade-leave-active {
  transition: 0.2s;
}

.fade-enter {
  max-height: 0px;
}

.fade-enter-to {
  max-height: 500px;
}

.fade-leave {
  max-height: 500px;
}

.fade-leave-to {
  max-height: 0;
}

.open-message {
  padding: 0 15px 0 15px;

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    margin-bottom: 10px;
  }
}

.back-arrow {
  cursor: pointer;
  height: 32px;
  width: 32px;
}

.message-presets {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 5px;
}

.status {
  font-weight: bold;

  &--green {
    color: #56ae27;
  }

  &--red {
    color: $red;
  }

  &--grey {
    color: $grey-3;
  }
}

.response {
  font-style: italic;
  font-weight: bold;
  margin-top: 10px;
}
.divider {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $grey-3;
  font-weight: bold;
  margin-top: 10px;

  &:before,
  &:after {
    content: '';
    display: block;
    height: 1px;
    width: 50px;
    background-color: $grey-3;
    margin-right: 10px;
    margin-left: 10px;
    position: relative;
    top: 2px;
  }
}

.message-preset {
  border: none;
  background-color: $grey-7;
  color: $grey-3;
  font-size: 0.875rem; /** 14px */
  width: 100%;
  display: block;
  padding: 8px;
  border-radius: 50px;
  font-weight: bold;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background-color: $grey-3;
    color: white;
  }
}

.custom-message {
  margin-top: 10px;
  box-sizing: border-box;
  width: 100%;
}

.sending-message {
  color: $grey-2;
  text-align: center;
  padding: 20px;
  font-weight: bold;
}
</style>
